import type {annotationCitation, annotationPageData, annotationUuid, citationData} from "../../main/data/model/index.js";
import Data from "../../main/data/index.js";
import {autoCitationsModes, Citation as libCitation, Project} from "../../main/index.js";
import Citations from "./common/citations.js";
// eslint-disable-next-line
import pTimeout from "p-timeout";
import Highlighter from "./index.js";

const AUTOCITATION_TIMEOUT = 10000; // milliseconds

class Citation {
	/**
	 * Determines proper citation data for new citation on the current page.
	 * This could be either the automatically detected data (getAutoGeneratedCitationData())
	 * or bulk-approved data used for the whole page (if available).
	 */
	getCitationData = async (pageData: annotationPageData): Promise<annotationCitation> => {
		if (!Data.app.project)
			throw new Error();
		const projectUuid = Data.app.project.uuid;

		// This isn't ideal, but we'll just pass a function into getBulkCitationData()
		// that can update old annotations when called. That way if getBulkCitationData()
		// decides that there is data to be updated, it can take care of it.
		const updateOldAnnotation = async (annotationUuid: annotationUuid, citation: annotationCitation) => {
			await Data.annotation.update(annotationUuid, projectUuid, {citation});
		};

		const citPromise = Citations.getBulkCitationData(
			Data.app.outline,
			() => this._getAutoGeneratedCitationData(pageData),
			updateOldAnnotation
		);
		const safePromise = pTimeout(citPromise, AUTOCITATION_TIMEOUT);

		let citation: annotationCitation;
		try {
			citation = await safePromise;

			if (
				citation.structured
				&& (
					citation.rendered_citation === undefined
					|| citation.rendered_citation === "<p></p>"
					|| !citation.rendered_citation
				)
			) {
				try {
					citation.rendered_citation = await this.getRenderedCitation(citation);
				} catch (e) {
					console.error("PN: Error getting rendered citation", e);
				}
			}
		} catch (e) {
			console.error("PN: Error generating citation data", e);

			// Just return a default empty citation
			citation = this._getCitationStub();
		}

		return citation;
	};

	async _getCitationFromExtension(useTopFrame = false): Promise<{
		status:   "ok" | "error";
		message?: string;
		data:     citationData;
	}> {
		throw new Error("Not implemented"); // TODO: evaluate if we'll ever implement this
		// const runtimeURL = chrome.runtime.getURL("");
		// const browser = runtimeURL.startsWith("moz") ? "firefox" : "chrome";
		//
		// const appConfig = Data.appConfig.config?.extension;
		// const extensionId = appConfig?.citation_extension_id[browser];
		//
		// const response = await new Promise((resolve, reject) => {
		// 	try {
		// 		chrome.runtime.sendMessage(
		// 			extensionId,
		// 			{
		// 				command: "citation",
		// 				formats: ["zotero", "csl", "ris"]
		// 			},
		// 			resolve
		// 		);
		// 	} catch (e) {
		// 		reject(e);
		// 	}
		// });
		//
		// switch (response?.status) {
		// 	case "ok":
		// 		return response;
		//
		// 	case "extension-not-found":
		// 		const extUrl = browser === "firefox"
		// 			? "https://chrome.google.com/webstore/detail/charset/oenllhgkiiljibhfagbfogdbchhdchml"
		// 			: `https://chrome.google.com/webstore/detail/${extensionId}`;
		//
		// 		return {
		// 			status:  "extension-not-found",
		// 			message: `Please install the citation extension <a href=${extUrl} target=\"_blank\">here</a> for citation gathering to work`
		// 		};
		//
		// 	case "rejected":
		// 		return {
		// 			status:  "rejected",
		// 			message: "Please enable PowerNotes in the citation extension's options for citation gathering to work"
		// 		};
		//
		// 	case "error":
		// 	default:
		// 		return {
		// 			status:  "error",
		// 			message: "Unknown error when gathering citation"
		// 		};
		// }
	};

	async _citationExtensionExists() {
		return false; // TODO: evaluate if we'll ever implement this
		// const appConfig = Data.appConfig.config?.extension;
		// const runtimeURL = chrome?.runtime?.getURL("") ?? "";
		// const browser = runtimeURL.startsWith("moz") ? "firefox" : "chrome";
		// return !!appConfig?.citation_extension_id[browser];
	};

	async _getZoteroBibCitation(zbibBaseUrl: string, searchType: string, searchValue: string): Promise<citationData> {
		const response = await fetch(`${zbibBaseUrl}/${searchType}`, {
			"headers": {
				"content-type": "text/plain"
			},
			"body":   searchValue,
			"method": "POST"
		});
		const citations = await response.json();
		if (!Array.isArray(citations) || citations.length < 1)
			throw new Error("Citation data not found");

		let citation = citations[0];
		delete citation.version;
		delete citation.key;

		// Populate csl and ris fields (in a very basic way)
		citation = libCitation.updateDataManually({
			zotero: citation
		}, citation);

		return citation;
	};

	async _getEasyBibCitation(easyBibBaseUrl: string, searchValue: string): Promise<citationData> {
		const url = new URL(easyBibBaseUrl + "query");
		url.searchParams.set("url", searchValue);

		const response = await fetch(url);
		if (response.status !== 200)
			throw new Error("Error while getting citation data");
		const json = await response.json();
		if (json.results === undefined || json.results.length < 1)
			throw new Error("Citation data not found");

		const data = libCitation.fromCSL(json.results[0].csl);
		if (!data)
			throw new Error("Citation data not found");

		return data;
	}

	/**
	 * Generates citation data from the page.
	 *
	 * If `remote` is true, doesn't rely on the current page, instead, it forces "structured = true" for the citation
	 * object, and doesn't try to get the citation using the citation extension, only zbib/easybib.
	 */
	async _getAutoGeneratedCitationData(pageData: annotationPageData, remote = false): Promise<annotationCitation> {
		if (!Highlighter.websiteBehaviors)
			throw new Error();

		const citation = this._getCitationStub(remote);
		const project = Data.app.project;
		const preferredStyle = Project.getCitationStyle(project);

		try {
			if (citation.structured) {
				const {data, auto_citation_error} = await this._getCitationObjData(pageData, remote);
				citation.data = data;
				citation.auto_citation_error = auto_citation_error;

				citation.render_format = preferredStyle;

				// Clear rendered citation if auto citations is disabled
				const projectCitationsType = Project.getProjectAutoCitationsMode(project);
				const isAutoCitationsDisabled = projectCitationsType === autoCitationsModes.MANUAL;
				if (isAutoCitationsDisabled)
					citation.rendered_citation = "<p></p>";
			} else {
				const citationData = (await Highlighter.websiteBehaviors.generateFreeformCitation(preferredStyle)) as {
					citation: string;
					style?:   string;
				};
				if (typeof citationData !== "object" || citationData.citation === undefined) {
					// noinspection ExceptionCaughtLocallyJS
					throw new Error("PN: Invalid freeform citation returned from: " + Highlighter.websiteBehaviors.identifier());
				}
				citation.rendered_citation = citationData.citation;
				if (citationData.style)
					citation.render_format = citationData.style;
			}
		} catch (error) {
			citation.structured = true;
			citation.auto_citation_error = "citation-ext";

			const url = pageData.cleanUrl ? pageData.cleanUrl : pageData.pageUrl;
			console.warn("PN: Citation failed to load", {url, error});
		}

		return citation;
	};

	_isLocalFilePage(): boolean {
		return Data.app.pageUrl?.startsWith("file:") ?? false;
	};

	/**
	 * Gets citation data (and possibly error status) from the citation extension,
	 * easybib, or zbib, depending on what is available.
	 */
	async _getCitationObjData(pageData: annotationPageData, remote: boolean, searchValue: string | null = null) {
		let data, auto_citation_error;

		if (!Highlighter.websiteBehaviors)
			throw new Error();

		// If it exists, use the unproxied url
		const url = pageData.cleanUrl ?? pageData.pageUrl;
		if (!url)
			throw new Error("Cannot determine page URL");

		let fromWB = null;
		if (!remote) {
			const ident = Highlighter.websiteBehaviors.identifier();
			// TODO: cleanup when data layer is in content script
			if (ident === "EbscoHtml" || ident === "EbscoEbooks")
				throw new Error("Not implemented yet");
				// const getEbscoCitationRis = (an, dbid) => BgComm.getEbscoCitationRis({an, dbid});
				//
				// fromWB = await Highlighter.websiteBehaviors.generateStructuredCitation(url, getEbscoCitationRis);
			else
				fromWB = await Highlighter.websiteBehaviors.generateStructuredCitation(url);
		}

		const useTopFrame = Highlighter.websiteBehaviors.structuredCitationFromTopFrame() ?? false;

		if (fromWB)
			data = fromWB;
		else if (libCitation.isAutoCitationBlacklisted(url))
			data = {zotero: {}, csl: {}, ris: ""};
		else if (!remote && await this._citationExtensionExists()) { // Can we use auto-citations?
			// Citation extension exists, try to search there
			const result = await this._getCitationFromExtension(useTopFrame);
			if (result.status !== "ok") {
				auto_citation_error = "citation-ext";
				throw new Error("PN: Citation gathering failed with status " + result.status);
			} else
				auto_citation_error = "";

			data = result.data;
		} else {
			const zbibBaseUrl = Data.appConfig.config?.extension?.zbib_base_url;
			const easyBibBaseUrl = Data.appConfig.config?.extension?.easybib_base_url;
			const isLocalFile = remote ? false : this._isLocalFilePage(); // We trust that if this is called with remote=true, it's not a local file

			const runZbibCitation = async (zbibBaseUrl: string, searchType: string, searchValue = "") => {
				try {
					data = await this._getZoteroBibCitation(zbibBaseUrl, searchType, searchType === "web" ? url : searchValue);
					auto_citation_error = "";
				} catch (e) {
					auto_citation_error = "zbib";
					throw e;
				}
			};

			if (searchValue) {
				if (zbibBaseUrl)
					await runZbibCitation(zbibBaseUrl, "search", searchValue);
				else {
					try {
						data = await libCitation.search(searchValue);
					} catch (e) {
						auto_citation_error = `No result found for "${searchValue}"`;
						throw e;
					}
				}
			} else if (easyBibBaseUrl && !isLocalFile) {
				// Don't try to get a zbib/easybib citation for local PDFs
				try {
					data = await this._getEasyBibCitation(easyBibBaseUrl, url);
					auto_citation_error = "";
				} catch (e) {
					auto_citation_error = "easybib";
					throw e;
				}
			} else if (zbibBaseUrl && !isLocalFile)
				await runZbibCitation(zbibBaseUrl, "web");
			else {
				// Empty citation if the citation extension doesn't exist.
				// Users can fill in the fields manually to generate a citation.
				data = {zotero: {}, csl: {}, ris: ""};
			}
		}

		return {data, auto_citation_error};
	};

	/**
	 * Helper to generate a basic citation that can be populated with available data.
	 *
	 * @param {boolean} [forceStructured] Used when we're not generating the stub for the current page.
	 */
	_getCitationStub(forceStructured = false): annotationCitation {
		const structured = forceStructured || !!(Highlighter.websiteBehaviors?.isCitationStructured());

		const stub = {
			version:  2,
			approved: false,
			structured
		} as annotationCitation;

		if (structured) {
			stub.data = {
				csl:    {},
				ris:    "",
				zotero: {}
			};
		} else
			stub.rendered_citation = "";

		return stub;
	};

	/**
	 * Apply a revised citation to an existing annotation (based on a local change).
	 * @return {number} How many annotations were updated
	 */
	async updateAnnotationCitation(annotationUuid: annotationUuid, citation: annotationCitation, onLocalSuccessCallback?: () => void, forcePageData: any = undefined): Promise<number> {
		throw new Error("Not implemented yet"); // TODO
		// let count = 0;
		// try {
		// 	// Don't wait for the remote call, only the local cache update
		// 	this.store.updateAnnotation(annotationUuid, {citation});
		// 	count++;
		//
		// 	// Bulk update if necessary.
		// 	// (Starting with v0.62.0 bulk update is performed in the extension instead of the api.)
		// 	// We update other citations for the same source if the user-modified citation is either
		// 	// a v2 citation or a v1 citation saved with the bulk-update checkbox checked.
		// 	if (libCitation.getCitationVersion(citation) >= 2 || citation.approve_method === "bulk") {
		// 		const updatedAnnotations = forcePageData
		// 			? Citations.calcBulkUpdatesWithPageData(annotationUuid, citation, this.store.outline, forcePageData)
		// 			: Citations.calcBulkUpdates(annotationUuid, citation, this.store.outline);
		// 		if (updatedAnnotations.length > 0) {
		// 			for (const ann of updatedAnnotations) { // save changes to local and API
		// 				await this.store.updateAnnotation(ann.annotation_uuid, {citation: ann.citation});
		// 				count++;
		// 			}
		// 		}
		// 	}
		//
		// 	// TODO: Shouldn't we just await this rather than using a callback?
		// 	if (typeof onLocalSuccessCallback === "function")
		// 		onLocalSuccessCallback();
		// } catch (e) {
		// }
		// return count;
	};

	/**
	 * Get rendered_citation from the API.
	 */
	getRenderedCitation = async (citation: annotationCitation): Promise<string> => {
		return await Data.app.backend.getRenderedCitation({citation});
	};
}

export default new Citation();
